/* eslint-disable max-len */
// Genera la hoja oficial de asistencia "Curso Ley de Armas de Puerto Rico 168-2019"
// en una ventana nueva, lista para imprimir o guardar como PDF.
// Se pre-llenan: fecha, hora, lugar, tipo de curso (checkbox) y nombre/apellidos
// de los asistentes. El resto (licencia, pagos, etc.) lo llena el empleado a mano.

const DAYS_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const LOGO_URL = 'https://bbtargetcenter.com/wp-content/uploads/2020/03/53201072_2501641786530857_5217031044271702016_o-1-1-uai-258x258.png';

function formatDateLong(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${DAYS_FULL[d.getDay()]}, ${d.getDate()} de ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${m} ${ampm}`;
}

// Heurística para separar nombre y apellidos de un nombre completo.
// 2 palabras → 1/1 · 3 palabras → 1/2 (patrón común en PR) · 4+ → mitad/2 últimos.
function splitName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { nombre: '', apellidos: '' };
  if (parts.length === 1) return { nombre: parts[0], apellidos: '' };
  if (parts.length === 2) return { nombre: parts[0], apellidos: parts[1] };
  return { nombre: parts.slice(0, parts.length - 2).join(' '), apellidos: parts.slice(-2).join(' ') };
}

function esc(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function buildAttendanceSheetHtml({ categoryName, dateISO, startTime, endTime, attendees, instructors, isPrivate }) {
  const lower = (categoryName || '').toLowerCase();
  // En cursos PRIVADOS el idioma y nueva/renovación vienen de cada
  // reservación (se piden al reservar). En cursos regulares se ignoran: antes
  // un curso de Renovación salía marcado "Privado" si alguien tocó el idioma.
  const privateCourse = typeof isPrivate === 'boolean' ? isPrivate : lower.includes('privado');
  const fromRes = (k) => (privateCourse ? (attendees || []).map((a) => a && a[k]).filter(Boolean) : []);
  const langs = new Set(fromRes('courseLanguage'));
  const types = new Set(fromRes('licenseType'));
  const checks = {
    nueva: lower.includes('nueva') || types.has('new'),
    renovacion: lower.includes('renovaci') || types.has('renewal'),
    privado: privateCourse,
    one: lower.includes('one'),
  };
  const box = (checked) => `<span class="cb">${checked ? '✕' : '&nbsp;'}</span>`;
  // "Privado Ing / Esp": el idioma escogido sale encerrado en un círculo,
  // igual que lo marcan a mano en la hoja física.
  const pick = (value, label) => (langs.has(value) ? `<span class="pick">${label}</span>` : label);

  // Columnas de requisitos — en el mismo orden que la hoja física.
  // Las marca el staff en dashboard/reservations/details y salen con ✕.
  const CHECK_COLS = ['abog', 'comp100', 'comp200', 'foto', 'abog5', 'calBalas', 'multas'];

  // Con "Solo Curso" la línea se rotula así, pero NO puede tapar las columnas
  // de la derecha: hay clientes que solo toman el curso pero rentaron armas, y
  // eso se anota en Abog 5 o más / Cal. Balas / Multas. Solo se cubren las de
  // la izquierda.
  const SOLO_CURSO_COLS = ['abog', 'comp100', 'comp200', 'foto'];
  const KEPT_COLS = CHECK_COLS.filter((k) => !SOLO_CURSO_COLS.includes(k));

  // Valor impreso de una columna de requisitos (✕, texto, o vacío)
  const checkContent = (a, k) => {
    const v = a && a.courseChecks ? a.courseChecks[k] : null;
    // Cal. Balas guarda texto (ej: "9MM"); Multas siempre sale con $.
    let content = typeof v === 'string' ? esc(v.trim()) : (v ? '✕' : '');
    if (k === 'multas' && content && content !== '✕' && !content.startsWith('$')) {
      content = `$${content}`;
    }
    return content;
  };

  // Instructores: nombres con checkbox. Los marcados en el dashboard salen con ✕;
  // los demás quedan en blanco para marcarlos a mano. Siempre hay línea "Otro".
  const instructorBlock = instructors && instructors.length
    ? `<div class="field" style="margin-bottom:4px">Instructor(es):</div>
      <div class="instructors">
        ${instructors.map((i) => `<div>${box(!!i.checked)} ${esc(i.name)}</div>`).join('')}
        <div>${box(false)} Otro: <span class="iline">&nbsp;</span></div>
      </div>`
    : `<div class="field">Instructor: <span class="val">&nbsp;</span></div>`;

  const rowCount = Math.max(15, attendees.length);
  const rows = Array.from({ length: rowCount }, (_, i) => {
    const a = attendees[i];
    const { nombre, apellidos } = a ? splitName(a.userName) : { nombre: '', apellidos: '' };
    // "Solo Curso": rótulo cruzando solo las columnas de la izquierda; las de
    // la derecha (Abog 5 o más, Cal. Balas, Multas) se siguen imprimiendo.
    const cellsFor = (cols) => cols.map((k) => `<td class="small mark">${checkContent(a, k)}</td>`).join('');
    const checkCells = a && a.courseChecks && a.courseChecks.soloCurso
      ? `<td class="solocurso" colspan="${SOLO_CURSO_COLS.length}">Solo Curso</td>${cellsFor(KEPT_COLS)}`
      : cellsFor(CHECK_COLS);
    // Asistente sin ningún requisito del curso marcado: la fila sale en rojo
    // (pedido 2026-10-09: antes se bloqueaba la impresión con un modal).
    return `
      <tr${a && a.missingReq ? ' class="missing"' : ''}>
        <td class="num">${i + 1}</td>
        <td class="name">${esc(nombre)}</td>
        <td class="name">${esc(apellidos)}${a && a.partnerName ? `<div class="partner">Aliado: ${esc(a.partnerName)}</div>` : ''}</td>
        <td>${a ? esc(a.licenseNumber || '') : ''}</td>
        ${checkCells}
      </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Asistencia — ${esc(categoryName)} — ${esc(dateISO)}</title>
<style>
  @page { size: letter landscape; margin: 10mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 24px; font-size: 12px; }
  .sheet { max-width: 1000px; margin: 0 auto; position: relative; }
  .logo { position: absolute; top: 0; right: 0; width: 64px; height: 64px; border-radius: 8px; }
  h1 { text-align: center; font-size: 16px; margin: 0 0 18px 0; }
  .meta { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
  .meta-left { flex: 1; }
  .field { margin-bottom: 10px; font-weight: 600; }
  .field .val { font-weight: 400; border-bottom: 1px solid #333; display: inline-block; min-width: 240px; padding: 0 6px 1px 6px; }
  .checkgrid { display: grid; grid-template-columns: auto auto; gap: 6px 24px; margin-right: 84px; font-size: 12px; }
  .instructors { display: grid; grid-template-columns: repeat(3, auto); gap: 4px 20px; justify-content: start; margin-bottom: 10px; font-size: 12px; }
  .iline { display: inline-block; border-bottom: 1px solid #333; min-width: 110px; }
  .partner { font-size: 9px; color: #555; font-weight: 600; }
  .pick { display: inline-block; border: 1.5px solid #333; border-radius: 12px; padding: 0 5px; font-weight: 700; }
  .cb { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #333; text-align: center; line-height: 13px; font-size: 11px; font-weight: 700; margin-right: 6px; vertical-align: -2px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th, td { border: 1px solid #444; padding: 4px 6px; font-size: 11px; }
  th { background: #f2f2f2; text-align: left; font-size: 10.5px; }
  td { height: 24px; }
  /* Filas alternadas estilo Excel — print-color-adjust para que salga impreso */
  tbody tr:nth-child(even) td { background: #efefef; }
  /* Faltan requisitos del curso: fila en rojo para que se vea de un vistazo */
  tbody tr.missing td { background: #ffd2d2 !important; color: #b00000; font-weight: bold; }
  .legend-missing { display: inline-block; margin-left: 10px; font-size: 10px; color: #b00000; }
  .legend-missing i { display: inline-block; width: 12px; height: 10px; background: #ffd2d2; border: 1px solid #b00000; vertical-align: middle; margin-right: 4px; }
  th, td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .num { width: 26px; text-align: center; }
  .name { width: 17%; }
  th.small, td.small { width: 44px; }
  td.mark { text-align: center; font-weight: 700; font-size: 13px; }
  td.mark { font-size: 11px; }
  td.solocurso { text-align: center; font-weight: 700; font-size: 12px; letter-spacing: 2px; }
  .footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 28px; }
  .sign { font-weight: 600; }
  .sign .line { display: inline-block; border-bottom: 1px solid #333; width: 280px; margin-left: 8px; }
  .stamp { width: 170px; height: 70px; border: 1px solid #333; }
  .printbar { position: fixed; top: 12px; left: 12px; }
  .printbar button { background: #d40100; color: #fff; border: 0; border-radius: 8px; padding: 10px 18px; font-size: 14px; font-weight: 600; cursor: pointer; }
  @media print { .printbar { display: none; } body { padding: 0; } }
</style>
</head>
<body>
  <div class="printbar"><button onclick="window.print()">🖨 Imprimir / Guardar PDF</button></div>
  <div class="sheet">
    <img class="logo" src="${LOGO_URL}" alt="B&amp;B Target Center" />
    <h1>Curso Ley de Armas de Puerto Rico 168-2019</h1>
    <div class="meta">
      <div class="meta-left">
        <div class="field">Fecha: <span class="val">${esc(formatDateLong(dateISO))}</span></div>
        <div class="field">Hora: <span class="val">${esc(formatTime12(startTime))}${endTime ? ` – ${esc(formatTime12(endTime))}` : ''}</span></div>
        ${instructorBlock}
        <div class="field">Lugar: <span class="val">B&amp;B Target Center, Inc.</span>${(attendees || []).some((a) => a && a.missingReq) ? '<span class="legend-missing"><i></i>En rojo: faltan requisitos del curso</span>' : ''}</div>
      </div>
      <div class="checkgrid">
        <div>${box(checks.nueva)} Nueva</div>
        <div>${box(checks.privado)} Privado ${pick('en', 'Ing')} / ${pick('es', 'Esp')}</div>
        <div>${box(checks.renovacion)} Renovación</div>
        <div>${box(checks.one)} One / One</div>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th class="num">#</th>
          <th>Nombre</th>
          <th>Apellidos</th>
          <th style="width:14%">Licencia #</th>
          <th class="small">Abog</th>
          <th class="small">Comp 100</th>
          <th class="small">Comp 200</th>
          <th class="small">Foto</th>
          <th class="small">Abog 5 o más</th>
          <th class="small">Cal. Balas</th>
          <th class="small">Multas</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="footer">
      <div class="sign">Firma de Instructor<span class="line"></span></div>
      <div class="stamp"></div>
    </div>
  </div>
</body>
</html>`;
}

function waitForImages(doc, timeoutMs = 2000) {
  const imgs = Array.from(doc.images || []);
  const pending = imgs.filter((img) => !img.complete).map(
    (img) => new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })
  );
  return Promise.race([
    Promise.all(pending),
    new Promise((resolve) => { setTimeout(resolve, timeoutMs); }),
  ]);
}

// Fallback sin popups: imprime desde un iframe oculto (el diálogo de imprimir
// permite también "Guardar como PDF").
async function printViaIframe(html) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument || iframe.contentWindow.document;
  idoc.open();
  idoc.write(html);
  idoc.close();
  await waitForImages(idoc);
  iframe.contentWindow.focus();
  iframe.contentWindow.print();
  setTimeout(() => { document.body.removeChild(iframe); }, 60000);
}

export function openAttendanceSheet(params) {
  const html = buildAttendanceSheetHtml(params);
  const win = window.open('', '_blank');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
  } else {
    // Popup bloqueado — imprimir directo desde iframe oculto
    printViaIframe(html);
  }
}
